<!--
<template>
  <button
    class="zm-button"
    @click="handleClick"
    :disabled="buttonDisabled || loading"
    :autofocus="autofocus"
    :type="nativeType"
    :class="[
      type ? 'zm-button--' + type : '',
      buttonSize ? 'zm-button--' + buttonSize : '',
      {
        'is-disabled': buttonDisabled,
        'is-loading': loading,
        'is-plain': plain,
        'is-round': round,
        'is-circle': circle
      }
    ]"
  >
    <i class="zm-icon-loading" v-if="loading"></i>
    <i :class="icon" v-if="icon && !loading"></i>
    <span class="zm-button__slot" v-if="$slots.default"><slot></slot></span>
  </button>
</template>
-->
<script>
  export default {
    name: 'ZmButton',

    inject: {
      ZmForm: {
        default: ''
      },
      ZmFormItem: {
        default: ''
      }
    },

    props: {
      type: {
        type: String,
        default: 'default'
      },
      size: {
        type: String,
        default: 'small'
      },
      href: {
        type: String,
        default: ''
      },
      target: {
        type: String,
        default: '_self'
      },
      icon: {
        type: String,
        default: ''
      },
      nativeType: {
        type: String,
        default: 'button'
      },
      loading: Boolean,
      disabled: Boolean,
      plain: Boolean,
      round: Boolean,
      circle: Boolean
    },
    render() {
      if (this.href) {
        return (
          <a
            {...this.buttonProps}
            class="zm-button"
            type={this.nativeType}
            target={this.buttonDisabled ? '_self' : this.target}
            href={this.buttonDisabled ? 'javascript:void(0)' : this.href}
            tabIndex={this.buttonDisabled ? -1 : 0}
          >
            {this.$slots.default ? <span class="zm-button__slot">{this.$slots.default}</span> : ''}
          </a>
        )
      } else {
        return (
          <button
            {...this.buttonProps}
            class="zm-button"
            disabled={this.buttonDisabled || this.loading}
            type={this.nativeType}
            on-click={this.handleClick}>
            {this.loading ? (<i class="zm-icon-loading"></i>) : ''}
            {(this.icon && !this.loading) ? <i class={this.icon}></i> : ''}
            {this.$slots.default ? <span class="zm-button__slot">{this.$slots.default}</span> : ''}
          </button>
        )
      }
    },
    computed: {
      _elFormItemSize() {
        return (this.ZmFormItem || {}).elFormItemSize;
      },
      buttonSize() {
        return this.size || this._elFormItemSize || (this.$ELEMENT || {}).size;
      },
      buttonDisabled() {
        return this.disabled || (this.ZmForm || {}).disabled;
      },
      buttonProps() {
        return {
          class: [
            this.type ? 'zm-button--' + this.type : '',
            this.buttonSize ? 'zm-button--' + this.buttonSize : '',
            {
              'is-disabled': this.buttonDisabled,
              'is-loading': this.loading,
              'is-plain': this.plain,
              'is-round': this.round,
              'is-circle': this.circle
            }
          ]
        }
      }
    },

    methods: {
      handleClick(evt) {
        this.$emit('click', evt);
      }
    }
  };
</script>

<style lang="scss">
  @import "../../scss/common/var";
  @import "../../scss/mixins/mixins";
  @import "../../scss/mixins/utils";
  @import "../../scss/mixins/button";

  @include b(button) {
    display: inline-block;
    white-space: nowrap;
    cursor: pointer;
    background: $--color-background-secondary;
    border: $--border-base;
    border-color: $--color-background-secondary;
    color: $--color-text-primary;
    -webkit-appearance: none;
    text-align: center;
    box-sizing: border-box;
    outline: none;
    margin: 0;
    transition: .1s;
    position: relative;
    @include utils-user-select(none);
    & + .zm-button {
      margin-left: 8px;
    }
    line-height: #{ $--button-small-height - 2px };
    @include button-size(0, $--button-small-padding-horizontal, $--button-small-font-size, $--button-small-border-radius);

    &:hover {
      color: $--color-text-primary-hover;
      border-color: $--color-background-secondary-hover;
      background-color: $--color-background-secondary-hover;
    }
    /*&:focus{
      box-shadow: 0 0 0 2px #fff,  0 0 0 4px $--color-border-primary;
    }*/

    &:active {
      color: $--color-text-primary-hover;
      border-color: $--color-background-secondary-active;
      background-color: $--color-background-secondary-active;
      outline: none;
    }

    &::-moz-focus-inner {
      border: 0;
    }

    & [class*="zm-icon-"] {
      line-height: 1;
      & + span {
        margin-left: 5px;
      }
    }
    @include when(plain) {
      background-color: #fff;
      border-color: $--color-border-secondary;
      &:hover,
      &:focus {
        border-color: $--color-primary;
        color: $--color-primary;
      }
    }
    /*
    @include when(plain) {
      &:hover,
      &:focus {
        background: $--color-white;
        border-color: $--color-primary;
        color: $--color-primary;
      }

      &:active {
        background: $--color-white;
        border-color: mix($--color-black, $--color-primary, $--button-active-shade-percent);
        color: mix($--color-black, $--color-primary, $--button-active-shade-percent);
        outline: none;
      }
    }
    */

    @include when(active) {
      color: $--color-white;
      border-color: $--color-background-secondary-active;
    }

    @include when(disabled) {
      &,
      &:hover,
      &:focus {
        color: $--color-text-disabled;
        cursor: not-allowed;
        background-image: none;
        background-color: $--color-background-disabled;
        border-color: $--color-background-disabled;
        box-shadow: none;
        text-decoration: none;
        outline: 0;
      }

      &.zm-button--text {
        background-color: transparent;
      }
      /*
          &.is-plain {
            &,
            &:hover,
            &:focus {
              background-color: $--color-white;
              border-color: $--color-background-secondary;
              color: $--color-text-disabled;
            }
          }*/
    }

    @include when(loading) {
      position: relative;
      pointer-events: none;

      &:before {
        pointer-events: none;
        content: '';
        position: absolute;
        left: -1px;
        top: -1px;
        right: -1px;
        bottom: -1px;
        border-radius: inherit;
        background-color: rgba(255, 255, 255, .35);
      }
    }
    @include when(round) {
      border-radius: 20px;
      padding: 0 $--button-padding-horizontal;
    }
    @include when(circle) {
      border-radius: 50%;
      padding: $--button-padding-vertical;
    }
    @include m(primary) {
      @include button-variant(
          $--color-white,
          $--color-border-primary,
          $--color-background-primary-hover,
          $--color-background-primary-active,
          $--color-border-primary,
          $--color-background-primary-hover,
          $--color-background-primary-active
      );
      .zm-icon-loading:before {
        color: transparent;
      }
      .zm-icon-loading:after {
        color: #fff;
      }
    }

    @include m(danger) {
      @include button-variant(
          $--color-white,
          $--color-background-danger,
          $--color-background-danger-hover,
          $--color-background-danger-active,
          $--color-background-danger,
          $--color-background-danger-hover,
          $--color-background-danger-active
      );
      /*&:focus{
        box-shadow: 0 0 0 2px #fff,  0 0 0 4px $--color-background-danger;
      }*/
    }

    @include m(large) {
      line-height: #{ $--button-lg-height - 2px };
      @include button-size(0, $--button-lg-padding-horizontal, $--button-lg-font-size, $--button-lg-border-radius);
      @include when(circle) {
        padding: $--button-lg-padding-vertical;
      }
    }
    @include m(small) {
      line-height: #{ $--button-small-height - 2px };
      @include button-size(0, $--button-small-padding-horizontal, $--button-small-font-size, $--button-small-border-radius);
      @include when(circle) {
        padding: $--button-small-padding-vertical;
      }
    }
    @include m(mini) {
      line-height: #{ $--button-mini-height - 2px } ;
      @include button-size(0, $--button-mini-padding-horizontal, $--button-mini-font-size, $--button-small-border-radius);
      @include when(circle) {
        padding: $--button-mini-padding-horizontal;
      }
    }

    @include m(text) {
      border-color: transparent;
      color: $--color-primary;
      background: transparent;

      &:hover {
        color: $--color-text-primary-hover;
        border-color: transparent;
        background-color: $--color-background-secondary-hover;
      }
      &:active {
        color: $--color-text-primary-hover;
        border-color: transparent;
        background-color: $--color-background-secondary-active;
      }

      &.is-disabled,
      &.is-disabled:hover,
      &.is-disabled:focus {
        color: $--color-text-disabled;
        border-color: transparent;
      }
    }
    @include m(link) {
      border-color: transparent;
      color: $--color-primary;
      background: transparent;
      padding-left: 0;
      padding-right: 0;
      &:hover,
      &:focus {
        color: $--color-text-primary-hover;
        border-color: transparent;
        background-color: transparent;
        text-decoration: underline;
      }
      &:focus {
        box-shadow: none;
      }
      &:active {
        color: $--color-text-primary-hover;
        border-color: transparent;
        background-color: transparent;
      }

      &.is-disabled,
      &.is-disabled:hover,
      &.is-disabled:focus {
        color: $--color-text-disabled;
        border-color: transparent;
        background-color: transparent;
        text-decoration: none;
      }
    }
    @include m(icon) {
      border-color: transparent;
      background: transparent;
      line-height: 24px;
      @include button-size(0, 5px, $--button-small-font-size, $--button-small-border-radius);

      .zm-button__slot {
        display: none;
      }
      &:hover,
      &:focus {
        border-color: $--color-primary;
        color: $--color-primary;
        background: transparent;
      }
      &:focus {
        box-shadow: none;
      }
      &:active {
        color: $--color-text-primary-hover;
        border-color: transparent;
        background-color: $--color-background-secondary-active;
      }

      &.is-disabled,
      &.is-disabled:hover,
      &.is-disabled:focus {
        border-color: transparent;
      }
    }
  }

  @include b(button-group) {
    @include utils-clearfix;
    display: inline-block;
    vertical-align: middle;

    & > .zm-button {
      float: left;
      position: relative;
      & + .zm-button {
        margin-left: 0;
      }

      &:first-child {
        border-top-right-radius: 0;
        border-bottom-right-radius: 0;
      }
      &:last-child {
        border-top-left-radius: 0;
        border-bottom-left-radius: 0;
      }
      &:first-child:last-child {
        border-top-right-radius: $--button-border-radius;
        border-bottom-right-radius: $--button-border-radius;
        border-top-left-radius: $--button-border-radius;
        border-bottom-left-radius: $--button-border-radius;

        &.is-round {
          border-radius: 20px;
        }

        &.is-circle {
          border-radius: 50%;
        }
      }
      &:not(:first-child):not(:last-child) {
        border-radius: 0;
      }
      &:not(:last-child) {
        margin-right: -1px;
      }

      &:hover,
      &:focus,
      &:active {
        z-index: 1;
      }

      @include when(active) {
        z-index: 1;
      }
    }
    & > .zm-dropdown {
      & > .zm-button {
        border-top-left-radius: 0;
        border-bottom-left-radius: 0;
        border-left-color: rgba($--color-white, 0.5);
      }
    }
    /* (primary, success, warning, danger, info) */
    @each $type in (primary, danger) {
      .zm-button--#{$type} {
        &:first-child {
          border-right-color: rgba($--color-white, 0.5);
        }
        &:last-child {
          border-left-color: rgba($--color-white, 0.5);
        }
        &:not(:first-child):not(:last-child) {
          border-left-color: rgba($--color-white, 0.5);
          border-right-color: rgba($--color-white, 0.5);
        }
      }
    }
  }

  .is-keyboard-event .zm-button:focus {
    box-shadow: 0 0 0 2px #fff, 0 0 0 4px $--color-border-primary;
  }

  .is-keyboard-event .zm-button--icon:focus {
    box-shadow: none;
  }

  .is-keyboard-event .zm-button--danger:focus {
    box-shadow: 0 0 0 2px #fff, 0 0 0 4px $--color-background-danger;
  }
</style>
